"use client";

import { motion } from "framer-motion";
import { useEffect, useRef } from "react";

import type { Option, OptionId, Question } from "@/lib/quiz-types";

import { OptionButton } from "./OptionButton";
import { ProgressBar } from "./ProgressBar";
import { screenVariants } from "./screen-variants";

interface QuestionScreenProps {
  question: Question;
  totalQuestions: number;
  selectedOptionId: OptionId | undefined;
  canGoBack: boolean;
  onAnswer: (optionId: OptionId) => void;
  onBack: () => void;
}

export function QuestionScreen({
  question,
  totalQuestions,
  selectedOptionId,
  canGoBack,
  onAnswer,
  onBack,
}: QuestionScreenProps) {
  const firstOptionRef = useRef<HTMLButtonElement>(null);
  const announcerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    firstOptionRef.current?.focus();
  }, [question.id]);

  useEffect(() => {
    if (announcerRef.current) {
      announcerRef.current.textContent = question.prompt;
    }
  }, [question.prompt]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "1" && question.options[0]) {
        onAnswer(question.options[0].id);
      } else if (e.key === "2" && question.options[1]) {
        onAnswer(question.options[1].id);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [question.options, onAnswer]);

  return (
    <motion.div
      key={question.id}
      variants={screenVariants}
      initial="enter"
      animate="center"
      exit="exit"
      className="flex flex-col gap-5"
    >
      {/* Aria live region for screen reader announcements */}
      <div ref={announcerRef} aria-live="polite" className="sr-only" />

      <div className="flex items-start justify-between gap-4">
        <ProgressBar current={question.number} total={totalQuestions} />
        {canGoBack && (
          <button
            type="button"
            onClick={onBack}
            className="focus-visible:ring-pulse-500 mt-0.5 shrink-0 text-sm font-medium text-white/60 underline underline-offset-4 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
          >
            Back
          </button>
        )}
      </div>

      <fieldset className="border-0 p-0">
        <legend className="sr-only">{question.prompt}</legend>
        <div className="border-t border-white/10 pt-5">
          <h1 className="text-heading-20 lg:text-heading-24 mb-5 text-white">
            {question.prompt}
          </h1>
          {question.helper && (
            <p className="text-body-sm mb-4 text-white/60">{question.helper}</p>
          )}
          <div className="flex flex-col gap-3">
            {question.options.map((option: Option, index: number) => (
              <OptionButton
                key={option.id}
                ref={index === 0 ? firstOptionRef : undefined}
                label={option.label}
                keyHint={index === 0 ? "1" : "2"}
                selected={selectedOptionId === option.id}
                onClick={() => {
                  // TODO: track event question_answered
                  onAnswer(option.id);
                }}
              />
            ))}
          </div>
        </div>
      </fieldset>
    </motion.div>
  );
}
